import * as auth from "@client/features/auth";
import * as cordova from "@client/features/cordova";
import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import * as analytics from "@client/shared/analytics";
import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import {get} from "lodash-es";

const CENTS = 100;

export type Actions =
    | {
          type: "LOAD_STRIPE_REQUEST";
      }
    | {
          type: "NEEDS_SUBSCRIPTION";
      }
    | {
          type: "SUBSCRIBING";
      }
    | {
          type: "ERROR_SUBSCRIBING";
      }
    | {
          type: "LOAD_STRIPE_SUCCESS";
      }
    | {
          type: "SHOW_PAYPAL";
      }
    | {
          type: "LOAD_APPLE_SUBSCRIPTIONS";
          products: hardware.StoreProduct[];
      }
    | {
          type: "SET_APPLE_ENTITLEMENTS";
          ownedProductIds: string[];
      };

const initialState = {
    isShowingPayPal: false,
    isLoaded: false,
    products: [] as hardware.StoreProduct[],
    ownedProductIds: [] as string[],
};

export const reducer = (state = initialState, action: Action) => {
    switch (action.type) {
        case "SHOW_PAYPAL": {
            return utils.assign(state, {
                isShowingPayPal: true,
            });
        }
        case "LOAD_APPLE_SUBSCRIPTIONS": {
            return utils.assign(state, {
                isLoaded: true,
                products: action.products,
            });
        }
        case "SET_APPLE_ENTITLEMENTS": {
            return utils.assign(state, {
                ownedProductIds: action.ownedProductIds,
            });
        }
        case "LOAD_STRIPE_SUCCESS": {
            return utils.assign(state, {
                isLoaded: true,
            });
        }
        default: {
            return state;
        }
    }
};

const showPayPal = () => ({
    type: "SHOW_PAYPAL",
});

const confirmAndDeleteProfile =
    (profile: server.core.rpc.Profile.output, isApple: boolean) =>
    async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body:
                    "Your Joy account will be deleted forever." +
                    (profile.user.is_subscription_active && isApple
                        ? "\n\nYou have an active subscription through Apple.\n\nApple will not allow us to cancel it for you.\n\nClick Manage Subscriptions to cancel it.\n\nOr just delete the app and you will be prompted to unsubscribe."
                        : ""),
            }),
        );

        if (confirmed) {
            const result = await server.core.rpc.delete_profile();

            if (result.type != "success") {
                dispatch(
                    notify(
                        "There was a problem deleting your profile. Please email **support@joyapp.com** and we'll do it for you.",
                        {type: "error", permanent: true},
                    ),
                );
            } else {
                dispatch(
                    notify(
                        "Your account was permanently deleted. Thanks for using Joy.",
                    ),
                );
                const router = await import("@client/router");
                void router.push("logout.index", {});
            }
        }
    };

let registering: Promise<void> | null = null;

const completePendingRegistration =
    () => (dispatch: AppDispatch, getState: GetState) => {
        const {pendingRegistration} = getState().orm;

        if (pendingRegistration == null) {
            return Promise.resolve();
        }

        registering ??= (async () => {
            const result = await server.core.rpc.mfp_registration(pendingRegistration);
            if (result.type == "success") {
                dispatch({
                    type: "SET_PENDING_REGISTRATION",
                    pendingRegistration: null,
                });

                const profile = result.data;
                void analytics.registered(profile.user.tracking_key, "method", "iap");
                analytics.unregister("pendingRegistration");
                void dispatch(auth.postLogin(profile, true));
                const router = await import("@client/router");
                void router.push("base.index", {});
            }
        })().finally(() => {
            registering = null;
        });
        return registering;
    };

const refreshEntitlements = () => async (dispatch: AppDispatch) => {
    const {transactions} = await hardware.PurchasesPlugin.getCurrentEntitlements();
    dispatch({
        type: "SET_APPLE_ENTITLEMENTS",
        ownedProductIds: transactions.map((transaction) => transaction.productId),
    });
};

const verifying = new Map<string, Promise<void>>();

/**
 * Send a transaction to the server and finish it once the server confirms. A
 * failure leaves it unfinished, and the next launch sends it again.
 */
const verifyTransaction =
    (transaction: hardware.StoreTransaction) => (dispatch: AppDispatch) => {
        const existing = verifying.get(transaction.id);

        if (existing != null) {
            return existing;
        }

        const verification = (async () => {
            await dispatch(completePendingRegistration());
            const result = await server.core.payments.verify_apple_transaction({
                signed_transaction: transaction.jws,
            });

            if (result.type == "success") {
                await hardware.PurchasesPlugin.finishTransaction({id: transaction.id});
                await dispatch(orm.fetchSubscription());
                await dispatch(refreshEntitlements());
            }
        })()
            .catch((error: unknown) => console.error("JOYIAP", error))
            .finally(() => verifying.delete(transaction.id));
        verifying.set(transaction.id, verification);
        return verification;
    };

export const getSubscriptionProductName = (
    state: RootState,
    plan: "yearly" | "basic",
) => `${state.boot.appId!}.iap.${plan}`;

export const loadAppleSubscriptions =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();

        if (state.subscriptions.isLoaded || state.boot.platform !== "ios") {
            return;
        }

        const productIds = [
            getSubscriptionProductName(state, "basic"),
            getSubscriptionProductName(state, "yearly"),
        ];

        if (cordova.isActive()) {
            await dispatch(
                cordova.load(productIds, () => dispatch(completePendingRegistration())),
            );
            return;
        }

        const result = await hardware.getStoreProducts(productIds);

        if (result.status == "not_implemented") {
            return;
        }

        void hardware.PurchasesPlugin.addListener(
            "transactionUpdated",
            (transaction) => void dispatch(verifyTransaction(transaction)),
        );
        dispatch({type: "LOAD_APPLE_SUBSCRIPTIONS", products: result.data.products});
        await dispatch(refreshEntitlements());

        const {transactions} =
            await hardware.PurchasesPlugin.getUnfinishedTransactions();
        for (const transaction of transactions) {
            void dispatch(verifyTransaction(transaction));
        }
    };

export const manageSubscriptions = () =>
    cordova.isActive() ? cordova.manage() : hardware.manageSubscriptions();

/**
 * Restore moves the App Store subscription to the signed-in user.
 */
export const restorePurchases = () => async (dispatch: AppDispatch) => {
    if (cordova.isActive()) {
        await cordova.restore();
        return;
    }
    const {transactions} = await hardware.PurchasesPlugin.restore();
    await Promise.all(
        transactions.map((transaction) => dispatch(verifyTransaction(transaction))),
    );
    await dispatch(refreshEntitlements());
};

let handler = null as StripeCheckoutHandler | null;

export const loadStripe = () => (dispatch: AppDispatch, getState: GetState) => {
    const subscription = orm.getSubscription(getState());

    const {
        orm: {apiCredentials},
        subscriptions: {isLoaded},
        boot,
    } = getState();

    if (
        isLoaded ||
        // Subscribed through web or hire registration people still need access to stripe.
        (subscription?.type != "stripe" && boot.platform === "ios")
    ) {
        return;
    }

    dispatch({
        type: "LOAD_STRIPE_REQUEST",
    });

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => {
        handler = StripeCheckout.configure({
            key: apiCredentials!.STRIPE_PUBLISHABLE_KEY,
        });
        dispatch({
            type: "LOAD_STRIPE_SUCCESS",
        });
    };
    script.src = "https://checkout.stripe.com/checkout.js";
    document.getElementsByTagName("head")[0].appendChild(script);
};

const subscribe = () => (dispatch: AppDispatch, getState: GetState) => {
    if (!handler) {
        throw Error("Credentials are not ready yet.");
    }

    dispatch({
        type: "SUBSCRIBING",
    });

    const profile = orm.getProfile(getState())!;
    const credentials = orm.getCredentials(getState())!;

    handler.open({
        name: "Joy",
        image: `${utils.getBaseURLWithOriginForCapacitor()}/static/favicon.png`,
        description: `Monthly recurring subscription`,
        amount: credentials.SUBSCRIPTION_DOLLARS_PER_MONTH * CENTS,
        email: profile.user.email,
        // oxlint-disable-next-line typescript/no-misused-promises
        token: async (payload) => {
            try {
                await dispatch(orm.createSubscription({token: payload.id}));
                dispatch(notify("Thank you for subscribing!"));
            } catch (errors) {
                dispatch({
                    type: "ERROR_SUBSCRIBING",
                });
                const message = get(errors, "data.message") as string | undefined;
                dispatch(
                    notify(message ?? "There was an error processing your payment", {
                        type: "error",
                    }),
                );
            }
        },
    });
};

const unsubscribe = () => (dispatch: AppDispatch) => {
    return dispatch(orm.deleteSubscription());
};

export const confirmPayPal =
    (payload: {payer_id: string; payment_id: string}) =>
    async (dispatch: AppDispatch) => {
        const result = await server.core.payments.create_paypal_subscription(payload);
        if (result.type == "success") {
            dispatch(notify("Thank you for your purchase."));
        } else {
            dispatch(
                notify(
                    "There was an error processing your PayPal payment. Please contact us at support@joyapp.com.",
                    {type: "error"},
                ),
            );
        }
    };

/**
 * Purchase a subscription. In E2E tests with __E2E_BYPASS_IAP__, this skips
 * the App Store and the verification and only completes the pending
 * registration.
 */
export const purchaseSubscription =
    (productId: string) =>
    async (
        dispatch: AppDispatch,
        getState: GetState,
    ): Promise<"PURCHASED" | "CANCELLED" | "PENDING" | "ERROR"> => {
        if (window.__E2E_BYPASS_IAP__ === true) {
            await dispatch(completePendingRegistration());
            return "PURCHASED";
        }

        if (cordova.isActive()) {
            return cordova.purchase(productId);
        }

        const state = getState();
        const appAccountToken =
            state.orm.pendingRegistration?.user_uuid ??
            orm.getProfile(state)?.user.uuid;

        if (appAccountToken == null) {
            return "ERROR";
        }

        try {
            const result = await hardware.purchaseProduct({productId, appAccountToken});

            if (result.status == "not_implemented") {
                return "ERROR";
            }

            if (result.data.status == "PURCHASED") {
                await dispatch(verifyTransaction(result.data.transaction));
            }
            return result.data.status;
        } catch (error) {
            console.error("JOYIAP", error);
            return "ERROR";
        }
    };

export const actions = {
    confirmPayPal,
    loadStripe,
    showPayPal,
    subscribe,
    unsubscribe,
    loadAppleSubscriptions,
    confirmAndDeleteProfile,
    purchaseSubscription,
};
